import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { CheckCircle2, ClipboardCopy, RefreshCw } from 'lucide-react';
import { ToolHeader } from '../components/ToolHeader';
import {
  genLorem,
  type LoremKind,
  type LoremLang,
} from '../lib/lorem';

const KIND_OPTIONS: ReadonlyArray<{ id: LoremKind; label: string }> = [
  { id: 'paragraph', label: '段落' },
  { id: 'sentence', label: '句子' },
  { id: 'word', label: '词' },
];

const LANG_OPTIONS: ReadonlyArray<{ id: LoremLang; label: string }> = [
  { id: 'la', label: 'Latin · 拉丁' },
  { id: 'zh', label: '中文 · 成语' },
];

const COUNT_PRESETS = [1, 3, 5, 10, 20, 50, 100];

/**
 * `/tools/generate/lorem` — 占位文本生成器。
 *
 * Why a free-form textarea instead of `<GeneratorShell>`: the shell's
 * row-based copy-each-line affordance is a great fit for UUIDs but
 * actively harmful for prose — a paragraph is one logical unit. Users
 * want to copy the whole block, not line by line.
 */
export default function LoremTool(): JSX.Element {
  const [kind, setKind] = useState<LoremKind>('paragraph');
  const [lang, setLang] = useState<LoremLang>('la');
  const [count, setCount] = useState<number>(3);
  const [tick, setTick] = useState(0);

  const text = useMemo(() => {
    return genLorem({ kind, count, lang });
    // `tick` re-rolls; intentionally part of the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, count, lang, tick]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="lorem"
        category="generate"
        description="占位文本生成（拉丁 lorem ipsum / 中文成语短语）。三种粒度：词 / 句 / 段；用于设计稿与字体预览，复制即用。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex flex-wrap items-center gap-2">
          <Pillbox
            label="粒度"
            options={KIND_OPTIONS}
            value={kind}
            onChange={setKind}
          />
          <Pillbox
            label="语言"
            options={LANG_OPTIONS}
            value={lang}
            onChange={setLang}
          />
          <label className="flex items-center gap-1 text-[11.5px] text-ink-muted">
            数量
            <select
              className="input py-0.5 text-[11.5px]"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
            >
              {COUNT_PRESETS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setTick((t) => t + 1)}
              className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11.5px] font-medium text-ink-muted hover:border-accent/60 hover:text-accent"
              title="重新生成"
            >
              <RefreshCw className="h-3 w-3" />
              重新生成
            </button>
            <CopyButton value={text} />
          </div>
        </header>
      </section>

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          输出
        </header>
        <textarea
          className="input min-h-0 flex-1 resize-none whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed"
          value={text}
          readOnly
          spellCheck={false}
        />
      </section>
    </div>
  );
}

interface PillboxProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}

function Pillbox<T extends string>({
  label,
  options,
  value,
  onChange,
}: PillboxProps<T>): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-ink-dim">{label}</span>
      <div className="flex items-center gap-1">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={clsx(
              'rounded-md border px-2 py-0.5 text-[11.5px] font-medium transition',
              value === opt.id
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [state, setState] = useState<'idle' | 'ok'>('idle');
  const onCopy = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setState('ok');
      window.setTimeout(() => setState('idle'), 1000);
    } catch {
      /* clipboard write failures are not actionable here */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={!value}
      className={clsx(
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11.5px] font-medium transition',
        !value
          ? 'cursor-not-allowed border border-line/60 text-ink-dim'
          : state === 'ok'
            ? 'border border-emerald-500/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'border border-line text-ink-muted hover:border-accent/60 hover:text-accent',
      )}
      title="复制全部"
    >
      {state === 'ok' ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <ClipboardCopy className="h-3 w-3" />
      )}
      {state === 'ok' ? '已复制' : '复制'}
    </button>
  );
}
